/**
 * FDM Integration Tab Component
 * File upload, data providers, webhooks, and sample data generation
 */

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { 
  Plug, FileUp, Webhook, FileText, Database, Server, 
  Upload, RefreshCw, Copy, TestTube, Play, Pencil, 
  Trash2, Zap, Globe, Settings
} from 'lucide-react';

const API_URL = import.meta.env.VITE_API_URL || process.env.REACT_APP_BACKEND_URL;

export default function FDMIntegrationTab({ 
  providers,
  providerTypes,
  uploadHistory,
  webhookLogs,
  generatorPresets,
  generating,
  lastGenerationResult,
  onShowUploadDialog,
  onShowProviderDialog,
  onShowGeneratorDialog,
  onTestProviderConnection,
  onSyncFromProvider,
  onOpenEditProvider,
  onDeleteProvider,
  onGenerateSampleData,
  onClearGeneratedData
}) {
  return (
    <div className="space-y-6 mt-6">
      {/* Integration Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-400 uppercase tracking-wider">Providers</p>
                <p className="text-2xl font-bold text-slate-100">{providers.length}</p>
              </div>
              <div className="p-3 rounded-lg bg-purple-500/20">
                <Plug className="w-5 h-5 text-purple-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-400 uppercase tracking-wider">File Uploads</p>
                <p className="text-2xl font-bold text-slate-100">{uploadHistory.length}</p>
              </div>
              <div className="p-3 rounded-lg bg-blue-500/20">
                <FileUp className="w-5 h-5 text-blue-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-400 uppercase tracking-wider">Webhooks</p>
                <p className="text-2xl font-bold text-slate-100">{webhookLogs.length}</p>
              </div>
              <div className="p-3 rounded-lg bg-green-500/20">
                <Webhook className="w-5 h-5 text-green-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-400 uppercase tracking-wider">Formats</p>
                <p className="text-2xl font-bold text-slate-100">4</p>
                <p className="text-xs text-slate-500">CSV, XML, QAR, JSON</p>
              </div>
              <div className="p-3 rounded-lg bg-orange-500/20">
                <FileText className="w-5 h-5 text-orange-400" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* File Upload Section */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg text-slate-100">File Upload</CardTitle>
              <CardDescription>Upload flight data from CSV, ACMS XML, QAR, or JSON files</CardDescription>
            </div>
            <Button onClick={onShowUploadDialog} data-testid="upload-file-btn">
              <Upload className="w-4 h-4 mr-2" />
              Upload File
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {[
              { id: 'csv', name: 'CSV / Excel', icon: FileText, color: 'text-green-400', desc: 'Spreadsheet with flight data' },
              { id: 'xml', name: 'ACMS XML', icon: FileText, color: 'text-blue-400', desc: 'Airbus ACMS format' },
              { id: 'qar', name: 'QAR Binary', icon: Database, color: 'text-purple-400', desc: 'Quick Access Recorder' },
              { id: 'json', name: 'JSON', icon: FileText, color: 'text-orange-400', desc: 'API integration format' }
            ].map(format => (
              <div key={format.id} className="p-4 rounded-lg bg-slate-700/30 border border-slate-600/50">
                <format.icon className={`w-6 h-6 ${format.color} mb-2`} />
                <p className="font-medium text-slate-200">{format.name}</p>
                <p className="text-xs text-slate-500">{format.desc}</p>
              </div>
            ))}
          </div>

          {/* Upload History */}
          {uploadHistory.length > 0 && (
            <div className="mt-6">
              <h4 className="text-sm font-medium text-slate-300 mb-3">Recent Uploads</h4>
              <div className="space-y-2">
                {uploadHistory.slice(0, 5).map((upload, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 rounded-lg bg-slate-700/20">
                    <div className="flex items-center gap-3">
                      <FileUp className="w-4 h-4 text-slate-400" />
                      <div>
                        <p className="text-sm text-slate-200">{upload.filename}</p>
                        <p className="text-xs text-slate-500">{upload.file_format?.toUpperCase()} • {(upload.file_size_bytes / 1024).toFixed(1)} KB</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className={upload.status === 'completed' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}>
                        {upload.flights_processed || 0} flights
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Providers Section */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg text-slate-100">FDM Data Providers</CardTitle>
              <CardDescription>Configure connections to FLYHT, Safran, Teledyne, GE Aviation, or custom systems</CardDescription>
            </div>
            <Button onClick={onShowProviderDialog} data-testid="add-provider-btn">
              <Plug className="w-4 h-4 mr-2" />
              Add Provider
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {/* Provider Types */}
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3 mb-6">
            {providerTypes.map(pt => (
              <div key={pt.id} className="p-3 rounded-lg bg-slate-700/30 border border-slate-600/50 text-center">
                <Server className="w-5 h-5 text-slate-400 mx-auto mb-1" />
                <p className="text-sm font-medium text-slate-200">{pt.name?.split(' ')[0]}</p>
                <div className="flex items-center justify-center gap-1 mt-1">
                  {pt.webhook_supported && <Badge variant="outline" className="text-xs px-1">Webhook</Badge>}
                  {pt.polling_supported && <Badge variant="outline" className="text-xs px-1">Polling</Badge>}
                </div>
              </div>
            ))}
          </div>

          {/* Configured Providers */}
          {providers.length > 0 ? (
            <div className="space-y-3">
              <h4 className="text-sm font-medium text-slate-300 mb-3">Configured Providers ({providers.length})</h4>
              {providers.map(provider => (
                <div key={provider.id} className="p-4 rounded-lg bg-slate-700/30 border border-slate-600/30">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      <div className={`p-2.5 rounded-lg ${provider.connection_status === 'connected' ? 'bg-green-500/20' : provider.is_active ? 'bg-yellow-500/20' : 'bg-slate-600/30'}`}>
                        <Server className={`w-6 h-6 ${provider.connection_status === 'connected' ? 'text-green-400' : provider.is_active ? 'text-yellow-400' : 'text-slate-400'}`} />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-semibold text-slate-200">{provider.name}</p>
                          <Badge className={provider.connection_status === 'connected' ? 'bg-green-500/20 text-green-400' : provider.is_active ? 'bg-yellow-500/20 text-yellow-400' : 'bg-slate-500/20 text-slate-400'}>
                            {provider.connection_status === 'connected' ? 'Connected' : provider.is_active ? 'Pending' : 'Inactive'}
                          </Badge>
                        </div>
                        <p className="text-sm text-slate-400 mt-1">
                          <span className="uppercase">{provider.provider_type}</span>
                          {provider.api_url && <span className="mx-2">•</span>}
                          {provider.api_url && <span className="text-slate-500">{provider.api_url}</span>}
                        </p>
                        <div className="flex flex-wrap items-center gap-2 mt-2">
                          {provider.webhook_enabled && (
                            <Badge variant="outline" className="text-xs bg-blue-500/10 text-blue-400 border-blue-500/30">
                              <Webhook className="w-3 h-3 mr-1" />
                              Webhook
                            </Badge>
                          )}
                          {provider.auto_sync_enabled && (
                            <Badge variant="outline" className="text-xs bg-purple-500/10 text-purple-400 border-purple-500/30">
                              <RefreshCw className="w-3 h-3 mr-1" />
                              Auto-Sync ({provider.sync_interval_minutes}min)
                            </Badge>
                          )}
                          {provider.last_sync_at && (
                            <span className="text-xs text-slate-500">
                              Last sync: {new Date(provider.last_sync_at).toLocaleString()}
                            </span>
                          )}
                        </div>
                        {provider.webhook_enabled && provider.webhook_secret && (
                          <div className="mt-2 flex items-center gap-2">
                            <span className="text-xs text-slate-500">Webhook Secret:</span>
                            <code className="text-xs bg-slate-800 px-2 py-0.5 rounded">{provider.webhook_secret?.substring(0, 8)}...</code>
                            <Button variant="ghost" size="sm" className="h-5 w-5 p-0" onClick={() => {
                              navigator.clipboard.writeText(provider.webhook_secret);
                              toast.success('Webhook secret copied');
                            }}>
                              <Copy className="w-3 h-3" />
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button variant="ghost" size="sm" onClick={() => onTestProviderConnection(provider.id)} title="Test Connection">
                        <TestTube className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => onSyncFromProvider(provider.id)} title="Sync Now">
                        <Play className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => onOpenEditProvider(provider)} title="Edit">
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" className="text-red-400 hover:text-red-300" onClick={() => onDeleteProvider(provider.id)} title="Delete">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <Plug className="w-10 h-10 text-slate-600 mx-auto mb-2" />
              <p className="text-slate-400">No providers configured</p>
              <p className="text-xs text-slate-500 mt-1">Add a provider to enable automatic data sync or webhook ingestion</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Webhook Info */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader>
          <CardTitle className="text-lg text-slate-100">Webhook Endpoints</CardTitle>
          <CardDescription>Configure your FDM system to push data to these endpoints</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {['flyht', 'safran', 'teledyne', 'ge_cfoqa', 'custom'].map(type => (
              <div key={type} className="p-3 rounded-lg bg-slate-700/30">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-slate-200 uppercase">{type.replace('_', ' ')}</span>
                  <Button variant="ghost" size="sm" onClick={() => {
                    navigator.clipboard.writeText(`${API_URL}/api/fdm-integration/webhook/${type}`);
                    toast.success('Webhook URL copied');
                  }}>
                    <Copy className="w-3 h-3" />
                  </Button>
                </div>
                <code className="text-xs text-slate-400 break-all">{`${API_URL}/api/fdm-integration/webhook/${type}`}</code>
              </div>
            ))}
          </div>

          {/* Webhook Logs */}
          {webhookLogs.length > 0 && (
            <div className="mt-6">
              <h4 className="text-sm font-medium text-slate-300 mb-3">Recent Webhooks</h4>
              <div className="space-y-2">
                {webhookLogs.slice(0, 5).map((log, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 rounded-lg bg-slate-700/20">
                    <div className="flex items-center gap-3">
                      <Webhook className="w-4 h-4 text-slate-400" />
                      <div>
                        <p className="text-sm text-slate-200">{log.provider_type?.toUpperCase()}</p>
                        <p className="text-xs text-slate-500">{new Date(log.received_at).toLocaleString()}</p>
                      </div>
                    </div>
                    <Badge className={log.status === 'completed' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}>
                      {log.status}
                    </Badge>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Sample Data Generator */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg text-slate-100 flex items-center gap-2">
                <Database className="w-5 h-5 text-purple-400" />
                Sample Data Generator
              </CardTitle>
              <CardDescription>Generate realistic FDM flight data for testing and demonstration</CardDescription>
            </div>
            <Button 
              variant="outline" 
              size="sm"
              onClick={onClearGeneratedData}
              className="text-red-400 border-red-400/30 hover:bg-red-400/10"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Clear Generated
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-4">
            {Object.entries(generatorPresets).map(([name, preset]) => (
              <div key={name} className="p-4 rounded-lg bg-slate-700/30 border border-slate-600/30">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-medium text-slate-200 capitalize">{name}</h4>
                  <Badge variant="outline" className="text-xs">{preset.num_flights} flights</Badge>
                </div>
                <p className="text-xs text-slate-400 mb-3">{preset.description}</p>
                <div className="text-xs text-slate-500 mb-3 space-y-1">
                  <p>• {preset.days_back} days range</p>
                  <p>• {Math.round(preset.exceedance_probability * 100)}% exceedance rate</p>
                  <p>• Max {preset.max_exceedances_per_flight} exceedances/flight</p>
                </div>
                <Button 
                  size="sm" 
                  className="w-full"
                  onClick={() => onGenerateSampleData(name)}
                  disabled={generating}
                >
                  {generating ? (
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Zap className="w-4 h-4 mr-2" />
                  )}
                  Generate
                </Button>
              </div>
            ))}
          </div>

          <div className="flex items-center gap-3">
            <Button variant="outline" onClick={onShowGeneratorDialog}>
              <Settings className="w-4 h-4 mr-2" />
              Custom Configuration
            </Button>
          </div>

          {/* Last Generation Result */}
          {lastGenerationResult && (
            <div className="mt-4 p-4 rounded-lg bg-green-500/10 border border-green-500/30">
              <h4 className="text-sm font-medium text-green-400 mb-2">Last Generation Result</h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <p className="text-slate-400">Flights Created</p>
                  <p className="text-xl font-bold text-slate-200">{lastGenerationResult.flights_created}</p>
                </div>
                <div>
                  <p className="text-slate-400">Exceedances Created</p>
                  <p className="text-xl font-bold text-slate-200">{lastGenerationResult.exceedances_created}</p>
                </div>
                <div>
                  <p className="text-slate-400">Clean Flights</p>
                  <p className="text-xl font-bold text-green-400">{lastGenerationResult.clean_flights}</p>
                </div>
                <div>
                  <p className="text-slate-400">With Exceedances</p>
                  <p className="text-xl font-bold text-orange-400">{lastGenerationResult.flights_with_exceedances}</p>
                </div>
              </div>
              {Object.keys(lastGenerationResult.category_distribution || {}).length > 0 && (
                <div className="mt-3">
                  <p className="text-xs text-slate-400 mb-1">Exceedances by Category:</p>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(lastGenerationResult.category_distribution).slice(0, 6).map(([cat, count]) => (
                      <Badge key={cat} variant="outline" className="text-xs">
                        {cat.replace(/_/g, ' ')}: {count}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* External Data Ingestion Info */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader>
          <CardTitle className="text-lg text-slate-100 flex items-center gap-2">
            <Globe className="w-5 h-5 text-blue-400" />
            External Data Ingestion
          </CardTitle>
          <CardDescription>Receive flight data from external systems via API</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-4 rounded-lg bg-slate-700/30">
              <h4 className="font-medium text-slate-200 mb-2 flex items-center gap-2">
                <FileText className="w-4 h-4 text-green-400" />
                JSON Endpoint
              </h4>
              <div className="flex items-center gap-2 mb-2">
                <code className="flex-1 text-xs bg-slate-800 px-3 py-2 rounded">
                  POST /api/fdm-generator/ingest/json
                </code>
                <Button variant="ghost" size="sm" onClick={() => {
                  navigator.clipboard.writeText(`${API_URL}/api/fdm-generator/ingest/json`);
                  toast.success('Endpoint copied');
                }}>
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
              <p className="text-xs text-slate-400">Accept single flight, array of flights, or batch format with exceedances</p>
            </div>
            <div className="p-4 rounded-lg bg-slate-700/30">
              <h4 className="font-medium text-slate-200 mb-2 flex items-center gap-2">
                <FileText className="w-4 h-4 text-yellow-400" />
                CSV Endpoint
              </h4>
              <div className="flex items-center gap-2 mb-2">
                <code className="flex-1 text-xs bg-slate-800 px-3 py-2 rounded">
                  POST /api/fdm-generator/ingest/csv
                </code>
                <Button variant="ghost" size="sm" onClick={() => {
                  navigator.clipboard.writeText(`${API_URL}/api/fdm-generator/ingest/csv`);
                  toast.success('Endpoint copied');
                }}>
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
              <p className="text-xs text-slate-400">Accept CSV data with flight records (exceedances not supported)</p>
            </div>
          </div>
          <div className="mt-4 p-3 rounded-lg bg-blue-500/10 border border-blue-500/30">
            <p className="text-sm text-blue-300">
              <strong>Tip:</strong> Use <code className="bg-slate-800 px-1 rounded">GET /api/fdm-generator/ingest/template/json</code> or 
              <code className="bg-slate-800 px-1 rounded ml-1">GET /api/fdm-generator/ingest/template/csv</code> to get data format templates.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
